import React from 'react';
import { Box, Typography, Button, Container, Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  LinkedIn as LinkedInIcon,
  YouTube as YouTubeIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import Logo from 'src/resources/images/logo-white 1.png';

const MediumIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1.63 13.93c-2.45 0-4.43-1.92-4.43-4.29s1.98-4.29 4.43-4.29 4.43 1.92 4.43 4.29-1.98 4.29-4.43 4.29zm6.06-.21c-1.18 0-2.14-1.69-2.14-3.77s.96-3.77 2.14-3.77 2.14 1.69 2.14 3.77-.96 3.77-2.14 3.77zm2.46-.86c-.34 0-.61-.83-.61-1.85s.27-1.85.61-1.85.61.83.61 1.85-.27 1.85-.61 1.85z" />
  </svg>
);

const Footer: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: '#0A0A0A',
        color: '#FFF',
        borderTop: '1px solid #1A1A1A',
        pt: 8,
        pb: 4,
        px: 3,
        mt: 'auto',
      }}
    >
      <Container maxWidth={false} sx={{ maxWidth: '1736px', p: '0 !important' }}>
        <Grid container spacing={4}>
          {/* Logo & Call to Action */}
          <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ cursor: 'pointer' }} onClick={() => navigate('/landing')}>
              <img src={Logo} alt="Darukaa Earth Logo" style={{ height: '24px' }} />
            </Box>

            <Button
              href="https://darukaa.earth/book-demo"
              variant="contained"
              endIcon={<ChevronRightIcon sx={{ fontSize: 16 }} />}
              sx={{
                width: 'fit-content',
                backgroundColor: '#FFF',
                color: '#000',
                fontWeight: 700,
                textTransform: 'uppercase',
                px: 3,
                py: 1,
                fontSize: '13px',
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: '#E5E5E5',
                },
              }}
            >
              Book A Demo
            </Button>

            {/* Social Links */}
            <Box sx={{ display: 'flex', gap: 2, color: 'rgba(255, 255, 255, 0.4)' }}>
              <Box
                component="a"
                href="https://www.linkedin.com/company/darukaa-esg-tech/posts/?feedView=all"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: 'inherit',
                  transition: 'color 0.2s',
                  '&:hover': { color: '#10B981' },
                }}
              >
                <LinkedInIcon />
              </Box>
              <Box
                component="a"
                href="https://www.youtube.com/@darukaa_earth"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: 'inherit',
                  transition: 'color 0.2s',
                  '&:hover': { color: '#10B981' },
                }}
              >
                <YouTubeIcon />
              </Box>
              <Box
                component="a"
                href="https://medium.com/@guneet.mutreja"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: 'inherit',
                  transition: 'color 0.2s',
                  '&:hover': { color: '#10B981' },
                }}
              >
                <MediumIcon />
              </Box>
            </Box>
          </Grid>

          {/* Solutions Column */}
          <Grid item xs={6} sm={4} md={2.6}>
            <Typography
              variant="subtitle2"
              sx={{
                fontSize: '11px',
                fontWeight: 700,
                color: 'rgba(255, 255, 255, 0.4)',
                letterSpacing: '1.5px',
                mb: 3,
                textTransform: 'uppercase',
              }}
            >
              Solutions
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography
                variant="body2"
                component="a"
                href="https://darukaa.earth/biodiversity"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  textDecoration: 'none',
                  transition: 'color 0.2s',
                  '&:hover': { color: '#10B981' },
                }}
              >
                Biodiversity
              </Typography>
              <Typography
                variant="body2"
                component="a"
                href="https://darukaa.earth/climate"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  textDecoration: 'none',
                  transition: 'color 0.2s',
                  '&:hover': { color: '#10B981' },
                }}
              >
                Climate
              </Typography>
              <Typography
                variant="body2"
                component="a"
                href="https://darukaa.earth/nature-finance"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  textDecoration: 'none',
                  transition: 'color 0.2s',
                  '&:hover': { color: '#10B981' },
                }}
              >
                Nature Finance
              </Typography>
            </Box>
          </Grid>

          {/* Company Column */}
          <Grid item xs={6} sm={4} md={2.6}>
            <Typography
              variant="subtitle2"
              sx={{
                fontSize: '11px',
                fontWeight: 700,
                color: 'rgba(255, 255, 255, 0.4)',
                letterSpacing: '1.5px',
                mb: 3,
                textTransform: 'uppercase',
              }}
            >
              Company
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography
                variant="body2"
                component="a"
                href="https://darukaa.earth/about-us"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  textDecoration: 'none',
                  transition: 'color 0.2s',
                  '&:hover': { color: '#10B981' },
                }}
              >
                About Us
              </Typography>
              <Typography
                variant="body2"
                component="a"
                href="https://darukaa.earth/careers"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  textDecoration: 'none',
                  transition: 'color 0.2s',
                  '&:hover': { color: '#10B981' },
                }}
              >
                Careers
              </Typography>
              
            </Box>
          </Grid>

          {/* Resources Column */}
          <Grid item xs={12} sm={4} md={2.8}>
            <Typography
              variant="subtitle2"
              sx={{
                fontSize: '11px',
                fontWeight: 700,
                color: 'rgba(255, 255, 255, 0.4)',
                letterSpacing: '1.5px',
                mb: 3,
                textTransform: 'uppercase',
              }}
            >
              Resources
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography
                variant="body2"
                component="a"
                href="https://darukaa.earth/resources"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  textDecoration: 'none',
                  transition: 'color 0.2s',
                  '&:hover': { color: '#10B981' },
                }}
              >
                Blog
              </Typography>
              <Typography
                variant="body2"
                component="a"
                href="/tool-hub"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  textDecoration: 'none',
                  transition: 'color 0.2s',
                  '&:hover': { color: '#10B981' },
                }}
              >
                Tools
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Bottom Bar */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            borderTop: '1px solid #1A1A1A',
            mt: 6,
            pt: 3,
            gap: 2,
          }}
        >
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)' }}>
            &copy; 2026 Darukaa Earth &middot; Privacy Policy &middot; Terms &amp; Conditions
          </Typography>
          
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
